import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { parseSetshabaDosing } from "./parseDosingImport";
import { useTrialProtocol, parseCriteriaItems, parseEndpoints, ProtocolEndpoint } from "@/hooks/useTrialProtocols";
import {
  CheckCircle,
  XCircle,
  Target,
  Shield,
  Pill,
  Plus,
  Trash2,
  Save,
  Loader2,
  FileText,
  Upload,
  Printer,
  Eye,
  AlertTriangle,
} from "lucide-react";

interface DoseRow {
  visit: string;
  day: string; // stored as string in inputs; empty = null
  dose: string;
  route: string;
  product: string;
}

function parseDosingSchedule(raw: unknown): { doses: DoseRow[]; description: string } {
  if (!raw) return { doses: [], description: "" };

  const toRow = (o: any): DoseRow => ({
    visit: o?.visit ?? "",
    day: o?.day === null || o?.day === undefined ? "" : String(o.day),
    dose: o?.dose ?? "",
    route: o?.route ?? "",
    product: o?.product ?? "",
  });

  if (Array.isArray(raw)) {
    return { doses: raw.map(toRow), description: "" };
  }
  if (typeof raw === "object") {
    const obj = raw as any;
    const doses = Array.isArray(obj.doses) ? obj.doses.map(toRow) : [];
    const description =
      typeof obj.description === "string" ? obj.description : "";
    if (doses.length || description) return { doses, description };
    // fallback: legacy structured JSON we can't map — stringify as description
    return { doses: [], description: JSON.stringify(raw, null, 2) };
  }
  if (typeof raw === "string") return { doses: [], description: raw };
  return { doses: [], description: "" };
}

interface TrialProtocolDetailEditorProps {
  trialId: string;
  siteId?: string;
  readOnly?: boolean;
  compact?: boolean;
}

export function TrialProtocolDetailEditor({ 
  trialId, 
  siteId,
  readOnly = false,
  compact = false 
}: TrialProtocolDetailEditorProps) {
  const { protocol, isLoading, upsertProtocol, isSaving } = useTrialProtocol(trialId);
  
  // Form state
  const [inclusionCriteria, setInclusionCriteria] = useState<string[]>([]);
  const [exclusionCriteria, setExclusionCriteria] = useState<string[]>([]);
  const [primaryEndpoints, setPrimaryEndpoints] = useState<ProtocolEndpoint[]>([]);
  const [secondaryEndpoints, setSecondaryEndpoints] = useState<ProtocolEndpoint[]>([]);
  const [safetyConsiderations, setSafetyConsiderations] = useState<string[]>([]);
  const [dosingSchedule, setDosingSchedule] = useState("");
  const [doses, setDoses] = useState<DoseRow[]>([]);
  const [protocolVersion, setProtocolVersion] = useState("");
  const [adherenceMonitoring, setAdherenceMonitoring] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importMode, setImportMode] = useState<"append" | "replace">("append");
  const [importPreview, setImportPreview] = useState<DoseRow[]>([]);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [footerEnabled, setFooterEnabled] = useState(true);
  const [footerVersion, setFooterVersion] = useState(true);
  const [footerTimestamp, setFooterTimestamp] = useState(true);
  const [footerTrial, setFooterTrial] = useState(true);
  const [footerSite, setFooterSite] = useState(true);
  const [footerNote, setFooterNote] = useState("");

  const buildPrintUrl = () => {
    const p = new URLSearchParams({ auto: "1" });
    if (siteId) p.set("siteId", siteId);
    if (!footerEnabled) p.set("footer", "0");
    if (!footerVersion) p.set("fVersion", "0");
    if (!footerTimestamp) p.set("fTimestamp", "0");
    if (!footerTrial) p.set("fTrial", "0");
    if (!footerSite) p.set("fSite", "0");
    if (footerNote.trim()) p.set("footerNote", footerNote.trim());
    return `/trials/${trialId}/dosing/print?${p.toString()}`;
  };



  // Load protocol data when available
  useEffect(() => {
    if (protocol) {
      setInclusionCriteria(parseCriteriaItems(protocol.inclusion_criteria));
      setExclusionCriteria(parseCriteriaItems(protocol.exclusion_criteria));
      setPrimaryEndpoints(parseEndpoints(protocol.primary_endpoints));
      setSecondaryEndpoints(parseEndpoints(protocol.secondary_endpoints));
      setSafetyConsiderations(parseCriteriaItems(protocol.safety_considerations));
      const parsedDosing = parseDosingSchedule(protocol.dosing_schedule);
      setDoses(parsedDosing.doses);
      setDosingSchedule(parsedDosing.description);
      setProtocolVersion(protocol.protocol_version || '');
      setAdherenceMonitoring(protocol.adherence_monitoring_enabled === true);
      setHasChanges(false);
    }
  }, [protocol]);

  const markChanged = () => setHasChanges(true);

  // Inclusion/Exclusion handlers
  const addCriteriaItem = (
    items: string[], 
    setItems: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setItems([...items, '']);
    markChanged();
  };

  const updateCriteriaItem = (
    items: string[], 
    setItems: React.Dispatch<React.SetStateAction<string[]>>,
    index: number, 
    value: string
  ) => {
    const updated = [...items];
    updated[index] = value;
    setItems(updated);
    markChanged();
  };

  const removeCriteriaItem = (
    items: string[], 
    setItems: React.Dispatch<React.SetStateAction<string[]>>,
    index: number
  ) => {
    setItems(items.filter((_, i) => i !== index));
    markChanged();
  };

  // Endpoint handlers
  const addEndpoint = (
    endpoints: ProtocolEndpoint[], 
    setEndpoints: React.Dispatch<React.SetStateAction<ProtocolEndpoint[]>>
  ) => {
    setEndpoints([...endpoints, { name: '', description: '', timepoint: '' }]);
    markChanged();
  };

  const updateEndpoint = (
    endpoints: ProtocolEndpoint[], 
    setEndpoints: React.Dispatch<React.SetStateAction<ProtocolEndpoint[]>>,
    index: number, 
    field: keyof ProtocolEndpoint, 
    value: string
  ) => {
    const updated = [...endpoints];
    updated[index] = { ...updated[index], [field]: value };
    setEndpoints(updated);
    markChanged();
  };

  const removeEndpoint = (
    endpoints: ProtocolEndpoint[], 
    setEndpoints: React.Dispatch<React.SetStateAction<ProtocolEndpoint[]>>,
    index: number
  ) => {
    setEndpoints(endpoints.filter((_, i) => i !== index));
    markChanged();
  };

  const handleSave = () => {
    // Cast endpoints to JSON-compatible format
    const primaryEndpointsJson = primaryEndpoints.filter(e => e.name.trim()).map(e => ({
      name: e.name,
      description: e.description,
      timepoint: e.timepoint || undefined,
    }));
    const secondaryEndpointsJson = secondaryEndpoints.filter(e => e.name.trim()).map(e => ({
      name: e.name,
      description: e.description,
      timepoint: e.timepoint || undefined,
    }));
    
    upsertProtocol({
      trial_id: trialId,
      protocol_version: protocolVersion || undefined,
      adherence_monitoring_enabled: adherenceMonitoring,
      inclusion_criteria: { items: inclusionCriteria.filter(c => c.trim()) },
      exclusion_criteria: { items: exclusionCriteria.filter(c => c.trim()) },
      primary_endpoints: primaryEndpointsJson,
      secondary_endpoints: secondaryEndpointsJson,
      safety_considerations: { items: safetyConsiderations.filter(s => s.trim()) },
      dosing_schedule:
        doses.length > 0 || dosingSchedule
          ? {
              doses: doses
                .filter((d) => d.visit || d.product || d.dose)
                .map((d) => ({
                  visit: d.visit || null,
                  day: d.day === "" ? null : Number.isNaN(Number(d.day)) ? d.day : Number(d.day),
                  dose: d.dose || null,
                  route: d.route || null,
                  product: d.product || null,
                })),
              description: dosingSchedule || null,
            }
          : null,
    });
    setHasChanges(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const renderCriteriaSection = (
    title: string,
    icon: React.ReactNode,
    items: string[],
    setItems: React.Dispatch<React.SetStateAction<string[]>>,
    placeholder: string,
    iconColor: string
  ) => (
    <AccordionItem value={title.toLowerCase().replace(/\s/g, '-')}>
      <AccordionTrigger className="hover:no-underline">
        <div className="flex items-center gap-2">
          <span className={iconColor}>{icon}</span>
          <span className="font-medium">{title}</span>
          <Badge variant="secondary" className="ml-2">{items.filter(i => i.trim()).length}</Badge>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <div className="space-y-2 pt-2">
          {items.length === 0 && !readOnly && (
            <p className="text-sm text-muted-foreground italic">No criteria defined yet.</p>
          )}
          {items.map((item, index) => (
            <div key={index} className="flex gap-2">
              {readOnly ? (
                <div className="flex items-start gap-2 py-1">
                  <span className={`mt-0.5 ${iconColor}`}>
                    {title.includes('Inclusion') ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  </span>
                  <span className="text-sm">{item}</span>
                </div>
              ) : (
                <>
                  <Input
                    value={item}
                    onChange={(e) => updateCriteriaItem(items, setItems, index, e.target.value)}
                    placeholder={placeholder}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeCriteriaItem(items, setItems, index)}
                    className="h-10 w-10 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          ))}
          {!readOnly && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addCriteriaItem(items, setItems)}
              className="w-full mt-2"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add {title.includes('Inclusion') ? 'Inclusion' : title.includes('Exclusion') ? 'Exclusion' : 'Safety'} Criterion
            </Button>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );

  const renderEndpointsSection = (
    title: string,
    icon: React.ReactNode,
    endpoints: ProtocolEndpoint[],
    setEndpoints: React.Dispatch<React.SetStateAction<ProtocolEndpoint[]>>,
    isPrimary: boolean
  ) => (
    <AccordionItem value={title.toLowerCase().replace(/\s/g, '-')}>
      <AccordionTrigger className="hover:no-underline">
        <div className="flex items-center gap-2">
          <span className="text-primary">{icon}</span>
          <span className="font-medium">{title}</span>
          <Badge variant="secondary" className="ml-2">{endpoints.filter(e => e.name.trim()).length}</Badge>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <div className="space-y-3 pt-2">
          {endpoints.length === 0 && !readOnly && (
            <p className="text-sm text-muted-foreground italic">No endpoints defined yet.</p>
          )}
          {endpoints.map((endpoint, index) => (
            <div key={index} className="p-3 border rounded-lg bg-muted/30 space-y-2">
              {readOnly ? (
                <>
                  <p className="font-medium">{endpoint.name}</p>
                  {endpoint.description && (
                    <p className="text-sm text-muted-foreground">{endpoint.description}</p>
                  )}
                  {endpoint.timepoint && (
                    <Badge variant="outline" className="text-xs">{endpoint.timepoint}</Badge>
                  )}
                </>
              ) : (
                <>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Endpoint Name</Label>
                      <Input
                        value={endpoint.name}
                        onChange={(e) => updateEndpoint(endpoints, setEndpoints, index, 'name', e.target.value)}
                        placeholder={isPrimary ? "e.g., Overall Survival" : "e.g., Quality of Life"}
                      />
                    </div>
                    <div className="w-32">
                      <Label className="text-xs">Timepoint</Label>
                      <Input
                        value={endpoint.timepoint || ''}
                        onChange={(e) => updateEndpoint(endpoints, setEndpoints, index, 'timepoint', e.target.value)}
                        placeholder="e.g., Week 12"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeEndpoint(endpoints, setEndpoints, index)}
                      className="h-10 w-10 mt-5 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div>
                    <Label className="text-xs">Description</Label>
                    <Textarea
                      value={endpoint.description}
                      onChange={(e) => updateEndpoint(endpoints, setEndpoints, index, 'description', e.target.value)}
                      placeholder="Describe the endpoint measurement..."
                      rows={2}
                    />
                  </div>
                </>
              )}
            </div>
          ))}
          {!readOnly && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addEndpoint(endpoints, setEndpoints)}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add {isPrimary ? 'Primary' : 'Secondary'} Endpoint
            </Button>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );

  if (compact) {
    // Compact read-only view for display in trial cards
    const hasAnyData = inclusionCriteria.length > 0 || exclusionCriteria.length > 0 || 
                       primaryEndpoints.length > 0 || secondaryEndpoints.length > 0;
    
    if (!hasAnyData) {
      return (
        <div className="text-sm text-muted-foreground italic py-2">
          No protocol details configured
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {inclusionCriteria.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="font-medium text-sm">Inclusion Criteria ({inclusionCriteria.length})</span>
            </div>
            <ul className="text-sm space-y-1 ml-6">
              {inclusionCriteria.slice(0, 3).map((item, i) => (
                <li key={i} className="text-muted-foreground">• {item}</li>
              ))}
              {inclusionCriteria.length > 3 && (
                <li className="text-muted-foreground italic">+{inclusionCriteria.length - 3} more...</li>
              )}
            </ul>
          </div>
        )}
        
        {exclusionCriteria.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="h-4 w-4 text-red-600" />
              <span className="font-medium text-sm">Exclusion Criteria ({exclusionCriteria.length})</span>
            </div>
            <ul className="text-sm space-y-1 ml-6">
              {exclusionCriteria.slice(0, 3).map((item, i) => (
                <li key={i} className="text-muted-foreground">• {item}</li>
              ))}
              {exclusionCriteria.length > 3 && (
                <li className="text-muted-foreground italic">+{exclusionCriteria.length - 3} more...</li>
              )}
            </ul>
          </div>
        )}

        {primaryEndpoints.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">Primary Endpoints ({primaryEndpoints.length})</span>
            </div>
            <ul className="text-sm space-y-1 ml-6">
              {primaryEndpoints.map((ep, i) => (
                <li key={i} className="text-muted-foreground">• {ep.name}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5" />
            Protocol Details
            {protocolVersion && (
              <Badge variant="outline">{protocolVersion}</Badge>
            )}
          </CardTitle>
          {!readOnly && (
            <div className="flex gap-2">
              {hasChanges && (
                <Badge variant="secondary" className="animate-pulse">Unsaved changes</Badge>
              )}
              <Button 
                onClick={handleSave} 
                disabled={isSaving || !hasChanges}
                size="sm"
                className="min-h-[40px]"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save Protocol
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col overflow-hidden">
        <ScrollArea className="h-[60vh] max-h-[600px] pr-4">
          {!readOnly && (
            <div className="mb-4 space-y-4">
              <div>
                <Label htmlFor="protocol-version">Protocol Version</Label>
                <Input
                  id="protocol-version"
                  value={protocolVersion}
                  onChange={(e) => { setProtocolVersion(e.target.value); markChanged(); }}
                  placeholder="e.g., v1.0, Amendment 2"
                  className="max-w-xs"
                />
              </div>
              <div className="flex items-start justify-between gap-4 rounded-md border p-3 max-w-xl">
                <div>
                  <Label htmlFor="adherence-monitoring">Medication adherence monitoring</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    When on, participants can choose to share a weekly adherence summary
                    (doses taken, missed, skipped) from their Health Passport. The site only
                    ever sees the summary — never notes, exact times, or other medicines.
                  </p>
                </div>
                <Switch
                  id="adherence-monitoring"
                  checked={adherenceMonitoring}
                  onCheckedChange={(v) => { setAdherenceMonitoring(v); markChanged(); }}
                />
              </div>
            </div>
          )}

          <Accordion type="multiple" defaultValue={['inclusion-criteria', 'exclusion-criteria']} className="space-y-2">
            {renderCriteriaSection(
              "Inclusion Criteria",
              <CheckCircle className="h-4 w-4" />,
              inclusionCriteria,
              setInclusionCriteria,
              "e.g., Age ≥18 years",
              "text-green-600"
            )}
            
            {renderCriteriaSection(
              "Exclusion Criteria",
              <XCircle className="h-4 w-4" />,
              exclusionCriteria,
              setExclusionCriteria,
              "e.g., Pregnant or nursing",
              "text-red-600"
            )}

            {renderEndpointsSection(
              "Primary Endpoints",
              <Target className="h-4 w-4" />,
              primaryEndpoints,
              setPrimaryEndpoints,
              true
            )}

            {renderEndpointsSection(
              "Secondary Endpoints",
              <Target className="h-4 w-4" />,
              secondaryEndpoints,
              setSecondaryEndpoints,
              false
            )}

            {renderCriteriaSection(
              "Safety Considerations",
              <AlertTriangle className="h-4 w-4" />,
              safetyConsiderations,
              setSafetyConsiderations,
              "e.g., Monitor liver function weekly",
              "text-amber-600"
            )}

            <AccordionItem value="dosing-schedule">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2">
                  <span className="text-purple-600"><Pill className="h-4 w-4" /></span>
                  <span className="font-medium">Dosing Schedule</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-4">
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPrintPreviewOpen(true)}
                    disabled={doses.length === 0}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Preview
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(buildPrintUrl(), "_blank", "noopener")}
                    disabled={doses.length === 0}
                  >
                    <Printer className="h-4 w-4 mr-1" />
                    Print / PDF
                  </Button>
                </div>
                {/* Structured dose table */}
                {doses.length > 0 ? (
                  readOnly ? (
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Visit</TableHead>
                            <TableHead>Day</TableHead>
                            <TableHead>Product</TableHead>
                            <TableHead>Dose</TableHead>
                            <TableHead>Route</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {doses.map((d, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-medium">{d.visit || "—"}</TableCell>
                              <TableCell>{d.day || "—"}</TableCell>
                              <TableCell>{d.product || "—"}</TableCell>
                              <TableCell>{d.dose || "—"}</TableCell>
                              <TableCell>{d.route || "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="rounded-md border overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-32">Visit</TableHead>
                              <TableHead className="w-20">Day</TableHead>
                              <TableHead>Product</TableHead>
                              <TableHead className="w-32">Dose</TableHead>
                              <TableHead className="w-40">Route</TableHead>
                              <TableHead className="w-10"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {doses.map((d, i) => (
                              <TableRow key={i}>
                                <TableCell>
                                  <Input
                                    value={d.visit}
                                    onChange={(e) => {
                                      const next = [...doses];
                                      next[i] = { ...next[i], visit: e.target.value };
                                      setDoses(next); markChanged();
                                    }}
                                    placeholder="Visit 1"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    value={d.day}
                                    onChange={(e) => {
                                      const next = [...doses];
                                      next[i] = { ...next[i], day: e.target.value };
                                      setDoses(next); markChanged();
                                    }}
                                    placeholder="1"
                                    inputMode="numeric"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    value={d.product}
                                    onChange={(e) => {
                                      const next = [...doses];
                                      next[i] = { ...next[i], product: e.target.value };
                                      setDoses(next); markChanged();
                                    }}
                                    placeholder="Product / IMP"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    value={d.dose}
                                    onChange={(e) => {
                                      const next = [...doses];
                                      next[i] = { ...next[i], dose: e.target.value };
                                      setDoses(next); markChanged();
                                    }}
                                    placeholder="0.5 mL"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    value={d.route}
                                    onChange={(e) => {
                                      const next = [...doses];
                                      next[i] = { ...next[i], route: e.target.value };
                                      setDoses(next); markChanged();
                                    }}
                                    placeholder="Intramuscular"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => {
                                      setDoses(doses.filter((_, idx) => idx !== i));
                                      markChanged();
                                    }}
                                    aria-label="Remove dose"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setDoses([...doses, { visit: "", day: "", dose: "", route: "", product: "" }]);
                            markChanged();
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add dose
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => { setImportPreview([]); setImportText(""); setImportMode("append"); setImportOpen(true); }}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Import from Setshaba
                        </Button>
                      </div>
                    </div>
                  )
                ) : (
                  !readOnly && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDoses([{ visit: "", day: "", dose: "", route: "", product: "" }]);
                          markChanged();
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add dose row
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => { setImportPreview([]); setImportText(""); setImportMode("append"); setImportOpen(true); }}
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        Import from Setshaba
                      </Button>
                    </div>
                  )
                )}

                {/* Free-text narrative */}
                <div>
                  <Label className="text-xs text-muted-foreground">Notes</Label>
                  {readOnly ? (
                    dosingSchedule ? (
                      <p className="text-sm whitespace-pre-wrap mt-1">{dosingSchedule}</p>
                    ) : doses.length === 0 ? (
                      <p className="text-sm text-muted-foreground mt-1">Not specified</p>
                    ) : null
                  ) : (
                    <Textarea
                      value={dosingSchedule}
                      onChange={(e) => { setDosingSchedule(e.target.value); markChanged(); }}
                      placeholder="Narrative notes about frequency, tapering, prohibited combinations…"
                      rows={3}
                      className="mt-1"
                    />
                  )}
                </div>

                <Dialog open={importOpen} onOpenChange={setImportOpen}>
                  <DialogContent className="max-w-3xl">
                    <DialogHeader>
                      <DialogTitle>Import dosing schedule from Setshaba</DialogTitle>
                      <DialogDescription>
                        Paste the Setshaba protocol dosing block. Accepts JSON, CSV/TSV,
                        or a pipe/tab table with headers such as Visit, Day, Product, Dose, Route.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <Textarea
                        value={importText}
                        onChange={(e) => setImportText(e.target.value)}
                        rows={10}
                        className="font-mono text-xs"
                        placeholder={`Visit | Day | Product | Dose | Route\nVisit 1 | 1 | PF-06760805 GBS6 vaccine | 0.5 mL | Intramuscular\nVisit 403 |   | Infanrix hexa | 0.5 mL | IM`}
                      />
                      <div className="flex flex-wrap items-center gap-3">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            const res = parseSetshabaDosing(importText);
                            setImportPreview(res.rows);
                            if (!res.rows.length) {
                              toast({ title: "No rows detected", description: "Check the format — headers must include Visit and at least one of Product/Dose.", variant: "destructive" });
                            } else {
                              toast({ title: `Parsed ${res.rows.length} row${res.rows.length === 1 ? "" : "s"}`, description: `Detected format: ${res.format}` });
                            }
                          }}
                        >
                          Parse preview
                        </Button>
                        <div className="flex items-center gap-2 text-sm">
                          <label className="flex items-center gap-1">
                            <input type="radio" checked={importMode === "append"} onChange={() => setImportMode("append")} />
                            Append
                          </label>
                          <label className="flex items-center gap-1">
                            <input type="radio" checked={importMode === "replace"} onChange={() => setImportMode("replace")} />
                            Replace
                          </label>
                        </div>
                        {importPreview.length > 0 && (
                          <Badge variant="secondary">{importPreview.length} rows ready</Badge>
                        )}
                      </div>
                      {importPreview.length > 0 && (
                        <div className="rounded-md border overflow-x-auto max-h-64">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Visit</TableHead>
                                <TableHead>Day</TableHead>
                                <TableHead>Product</TableHead>
                                <TableHead>Dose</TableHead>
                                <TableHead>Route</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {importPreview.map((d, i) => (
                                <TableRow key={i}>
                                  <TableCell>{d.visit || "—"}</TableCell>
                                  <TableCell>{d.day || "—"}</TableCell>
                                  <TableCell>{d.product || "—"}</TableCell>
                                  <TableCell>{d.dose || "—"}</TableCell>
                                  <TableCell>{d.route || "—"}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                    <DialogFooter>
                      <Button variant="ghost" onClick={() => setImportOpen(false)}>Cancel</Button>
                      <Button
                        disabled={importPreview.length === 0}
                        onClick={() => {
                          setDoses(importMode === "replace" ? importPreview : [...doses, ...importPreview]);
                          markChanged();
                          setImportOpen(false);
                          toast({ title: "Dosing schedule imported", description: `${importPreview.length} rows ${importMode === "replace" ? "replaced" : "appended"}.` });
                        }}
                      >
                        Apply
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog open={printPreviewOpen} onOpenChange={setPrintPreviewOpen}>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                      <DialogTitle>Print preview — Dosing Schedule</DialogTitle>
                      <DialogDescription>
                        Confirm the layout before opening the print-optimized page.
                      </DialogDescription>
                    </DialogHeader>

                    <div className="border rounded-md p-3 bg-muted/30 text-xs space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 font-medium">
                          <input type="checkbox" checked={footerEnabled} onChange={(e) => setFooterEnabled(e.target.checked)} />
                          Show page footer on every printed page
                        </label>
                      </div>
                      {footerEnabled && (
                        <>
                          <div className="flex flex-wrap gap-3">
                            <label className="flex items-center gap-1"><input type="checkbox" checked={footerVersion} onChange={(e) => setFooterVersion(e.target.checked)} /> Protocol version</label>
                            <label className="flex items-center gap-1"><input type="checkbox" checked={footerTimestamp} onChange={(e) => setFooterTimestamp(e.target.checked)} /> Timestamp</label>
                            <label className="flex items-center gap-1"><input type="checkbox" checked={footerTrial} onChange={(e) => setFooterTrial(e.target.checked)} /> Trial identifier</label>
                            <label className="flex items-center gap-1"><input type="checkbox" checked={footerSite} onChange={(e) => setFooterSite(e.target.checked)} /> Site identifier</label>
                          </div>
                          <Input
                            value={footerNote}
                            onChange={(e) => setFooterNote(e.target.value)}
                            placeholder="Optional footer note (e.g. Confidential — do not distribute)"
                            className="h-7 text-xs"
                          />
                        </>
                      )}
                    </div>

                    <div className="flex-1 overflow-auto bg-neutral-100 p-4 rounded">
                      <div className="mx-auto bg-white text-black shadow border relative" style={{ width: "210mm", minHeight: "297mm", padding: "16mm 14mm", paddingBottom: footerEnabled ? "26mm" : "16mm", boxSizing: "border-box" }}>
                        <div className="border-b pb-3 mb-4">
                          <h2 className="text-2xl font-bold">Dosing Schedule</h2>
                          <div className="mt-2 text-sm space-y-0.5">
                            <div>
                              <span className="font-semibold">Protocol version:</span>{" "}
                              {protocolVersion || "—"}
                            </div>
                            <div>
                              <span className="font-semibold">Printed:</span>{" "}
                              {new Date().toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11pt" }}>
                          <thead>
                            <tr>
                              {["Visit", "Day", "Product", "Dose", "Route"].map((h, i) => (
                                <th key={h} style={{ border: "1px solid #333", padding: "6px 8px", textAlign: "left", background: "#f0f0f0", fontWeight: 600, width: i === 0 ? "16%" : i === 1 ? "10%" : i === 3 ? "14%" : i === 4 ? "18%" : undefined }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {doses.map((d, i) => (
                              <tr key={i} style={{ background: i % 2 ? "#fafafa" : "#fff" }}>
                                <td style={{ border: "1px solid #333", padding: "6px 8px" }}>{d.visit || "—"}</td>
                                <td style={{ border: "1px solid #333", padding: "6px 8px" }}>{d.day || "—"}</td>
                                <td style={{ border: "1px solid #333", padding: "6px 8px" }}>{d.product || "—"}</td>
                                <td style={{ border: "1px solid #333", padding: "6px 8px" }}>{d.dose || "—"}</td>
                                <td style={{ border: "1px solid #333", padding: "6px 8px" }}>{d.route || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {dosingSchedule && (
                          <div className="mt-6">
                            <h3 className="text-base font-semibold mb-2">Notes</h3>
                            <p className="text-sm whitespace-pre-wrap">{dosingSchedule}</p>
                          </div>
                        )}
                        {footerEnabled && (
                          <div
                            className="absolute left-0 right-0 flex justify-between gap-3"
                            style={{
                              bottom: 0,
                              padding: "4px 14mm 6px 14mm",
                              borderTop: "1px solid #999",
                              fontSize: "9pt",
                              color: "#444",
                              background: "#fff",
                            }}
                          >
                            <div className="flex flex-col">
                              {footerTrial && (
                                <span><strong>Trial:</strong> {trialId}</span>
                              )}
                              {footerSite && siteId && (
                                <span><strong>Site:</strong> {siteId}</span>
                              )}
                              {footerNote && <span>{footerNote}</span>}
                            </div>
                            <div className="flex flex-col items-end text-right">
                              {footerVersion && (
                                <span><strong>Protocol:</strong> {protocolVersion || "—"}</span>
                              )}
                              {footerTimestamp && (
                                <span><strong>Printed:</strong> {new Date().toLocaleString()}</span>
                              )}
                              <span>PharmaTrialConnect</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="ghost" onClick={() => setPrintPreviewOpen(false)}>Close</Button>
                      <Button
                        onClick={() => {
                          setPrintPreviewOpen(false);
                          window.open(buildPrintUrl(), "_blank", "noopener");
                        }}
                      >
                        <Printer className="h-4 w-4 mr-1" />
                        Open print page
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
